import { useEffect, useMemo, useState } from "react";
import {
    Backdrop, Badge, Box, Chip, CircularProgress, Container, FormControl, InputLabel, MenuItem,
    Paper, Select, Skeleton, Tab, Tabs, Typography,
} from "@mui/material";
import { CorporateFare, CreditCard, Groups, Storefront } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAutenticacaoContext } from "../../../core/contexts/AutenticacaoContext";
import { useMoeda } from "../../../core/hooks/useMoeda";
import { IPlano } from "../../../estabelecimento/interfaces/IPlano";
import { useOrganizacoes } from "../../hooks/useOrganizacoes";
import {
    IEstabelecimentoOrganizacao, IMembroOrganizacao, IOrganizacao, ISituacaoEstabelecimento,
} from "../../interfaces/IOrganizacao";
import { AbaAssinaturas } from "./containers/AbaAssinaturas";
import { AbaEstabelecimentos } from "./containers/AbaEstabelecimentos";
import { AbaUsuarios } from "./containers/AbaUsuarios";
import { IAtualizarAssinaturaPayload, ModalAssinatura } from "./containers/ModalAssinatura";
import { ModalBloqueio } from "./containers/ModalBloqueio";
import { HeaderPaper, IdentidadeBox, IndicadorBox, IndicadoresBox, MonogramaBox, VazioBox } from "./styles";
import { documentoEstabelecimento, iniciais, nomeEstabelecimento, resumirOrganizacao } from "./utils";

interface IAssinaturaAberta {
    estabelecimento: IEstabelecimentoOrganizacao;
    situacao: ISituacaoEstabelecimento | null;
    carregando: boolean;
}

interface IBloqueioAberto {
    estabelecimento: IEstabelecimentoOrganizacao;
    /** true = bloquear; false = liberar de volta. */
    bloquear: boolean;
}

const Indicador = ({ rotulo, valor, cor }: { rotulo: string; valor: string; cor?: string }) => (
    <IndicadorBox>
        <Typography variant="caption" color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
            {rotulo}
        </Typography>
        <Typography variant="h5" fontWeight="bold" color={cor}>{valor}</Typography>
    </IndicadorBox>
);

export const GerenciarOrganizacao = () => {
    const navigate = useNavigate();
    const { real } = useMoeda();
    const { organizacoes, usuario, entrarEstabelecimento } = useAutenticacaoContext();
    const {
        listarMinhas, listarMembros, adicionarMembro, removerMembro, listarEstabelecimentos, listarPlanos,
        buscarSituacao, bloquearEstabelecimento, desbloquearEstabelecimento, criarAssinatura, atualizarAssinatura,
        cancelarAssinatura,
    } = useOrganizacoes();

    const [inicializado, setInicializado] = useState(false);
    const [minhas, setMinhas] = useState<IOrganizacao[]>([]);
    const [organizacaoUuid, setOrganizacaoUuid] = useState<string>();
    const [estabelecimentos, setEstabelecimentos] = useState<IEstabelecimentoOrganizacao[]>([]);
    const [membros, setMembros] = useState<IMembroOrganizacao[]>([]);
    const [planos, setPlanos] = useState<IPlano[]>([]);

    const [aba, setAba] = useState(0);
    const [carregando, setCarregando] = useState(true);
    const [entrando, setEntrando] = useState(false);
    const [salvando, setSalvando] = useState(false);

    const [assinatura, setAssinatura] = useState<IAssinaturaAberta>();
    const [bloqueio, setBloqueio] = useState<IBloqueioAberto>();

    const organizacao = useMemo(
        () => minhas.find(item => item.uuid === organizacaoUuid),
        [minhas, organizacaoUuid]
    );
    const resumo = useMemo(() => resumirOrganizacao(estabelecimentos), [estabelecimentos]);

    const inicializar = async () => {
        const lista = await listarMinhas();
        setMinhas(lista);
        setInicializado(true);

        // Só reposiciona quando a organização escolhida sumiu da lista: sem
        // isso, um refresh do contexto jogaria de volta para a primeira.
        setOrganizacaoUuid(atual => (atual && lista.some(item => item.uuid === atual) ? atual : lista[0]?.uuid));

        if (!lista.length) {
            setCarregando(false);
        }
    };

    useEffect(() => {
        inicializar();
    }, [organizacoes]);

    const carregarEstabelecimentos = async (uuid: string) => {
        setEstabelecimentos(await listarEstabelecimentos(uuid));
    };

    const carregarDados = async (uuid: string) => {
        setCarregando(true);
        try {
            const [novosEstabelecimentos, novosMembros, novosPlanos] = await Promise.all([
                listarEstabelecimentos(uuid),
                listarMembros(uuid),
                listarPlanos(),
            ]);
            setEstabelecimentos(novosEstabelecimentos);
            setMembros(novosMembros);
            setPlanos(novosPlanos);
        } finally {
            setCarregando(false);
        }
    };

    useEffect(() => {
        if (organizacaoUuid) {
            carregarDados(organizacaoUuid);
        }
    }, [organizacaoUuid]);

    const handleEntrar = async (estabelecimentoUuid: string) => {
        setEntrando(true);
        try {
            await entrarEstabelecimento(estabelecimentoUuid);
        } finally {
            // Estabelecimento bloqueado responde 403 e não navega: sem isso o
            // overlay de carregamento ficaria girando para sempre.
            setEntrando(false);
        }
    };

    // ---- Membros ----

    const handleAdicionarMembro = async (email: string) => {
        if (!organizacaoUuid) return false;

        setSalvando(true);
        try {
            const adicionado = await adicionarMembro(organizacaoUuid, email);
            if (adicionado) {
                setMembros(await listarMembros(organizacaoUuid));
            }
            return adicionado;
        } finally {
            setSalvando(false);
        }
    };

    const handleRemoverMembro = async (usuarioUuid: string) => {
        if (!organizacaoUuid) return;

        setSalvando(true);
        try {
            if (await removerMembro(organizacaoUuid, usuarioUuid)) {
                setMembros(await listarMembros(organizacaoUuid));
            }
        } finally {
            setSalvando(false);
        }
    };

    // ---- Assinatura ----

    const abrirAssinatura = async (estabelecimento: IEstabelecimentoOrganizacao) => {
        if (!organizacaoUuid) return;

        setAssinatura({ estabelecimento, situacao: null, carregando: true });
        const situacao = await buscarSituacao(organizacaoUuid, estabelecimento.uuid);
        // O modal pode ter sido fechado (ou trocado de estabelecimento) durante a busca.
        setAssinatura(atual => atual?.estabelecimento.uuid === estabelecimento.uuid
            ? { ...atual, situacao, carregando: false }
            : atual);
    };

    /**
     * Toda escrita recarrega a lista, e também a situação do modal quando ele
     * está aberto naquele estabelecimento — o bloqueio, disparado pelo card,
     * não precisa desse segundo request.
     */
    const escrever = async (estabelecimentoUuid: string, acao: () => Promise<boolean>) => {
        const modalAberto = assinatura?.estabelecimento.uuid === estabelecimentoUuid;

        setSalvando(true);
        try {
            if (!await acao() || !organizacaoUuid) {
                return false;
            }

            const [situacao] = await Promise.all([
                modalAberto ? buscarSituacao(organizacaoUuid, estabelecimentoUuid) : Promise.resolve(null),
                carregarEstabelecimentos(organizacaoUuid),
            ]);

            if (modalAberto) {
                setAssinatura(atual => atual?.estabelecimento.uuid === estabelecimentoUuid
                    ? { ...atual, situacao, carregando: false }
                    : atual);
            }

            return true;
        } finally {
            setSalvando(false);
        }
    };

    const handleCriarAssinatura = async (payload: { planoUuid: string; valor?: number; diaVencimento?: number; dataFimPeriodoTeste?: string }) => {
        const estabelecimentoUuid = assinatura?.estabelecimento.uuid;
        if (!organizacaoUuid || !estabelecimentoUuid) return;

        await escrever(estabelecimentoUuid, () => criarAssinatura(organizacaoUuid, estabelecimentoUuid, payload));
    };

    const handleAtualizarAssinatura = async (assinaturaUuid: string, payload: IAtualizarAssinaturaPayload) => {
        const estabelecimentoUuid = assinatura?.estabelecimento.uuid;
        if (!organizacaoUuid || !estabelecimentoUuid) return;

        await escrever(estabelecimentoUuid, () => atualizarAssinatura(organizacaoUuid, estabelecimentoUuid, assinaturaUuid, payload));
    };

    const handleCancelarAssinatura = async (assinaturaUuid: string) => {
        const estabelecimentoUuid = assinatura?.estabelecimento.uuid;
        if (!organizacaoUuid || !estabelecimentoUuid) return;

        await escrever(estabelecimentoUuid, () => cancelarAssinatura(organizacaoUuid, estabelecimentoUuid, assinaturaUuid));
    };

    // ---- Bloqueio ----

    const handleConfirmarBloqueio = async (motivo?: string) => {
        if (!organizacaoUuid || !bloqueio) return;

        const { estabelecimento, bloquear } = bloqueio;
        const ok = await escrever(estabelecimento.uuid, () => bloquear
            ? bloquearEstabelecimento(organizacaoUuid, estabelecimento.uuid, motivo)
            : desbloquearEstabelecimento(organizacaoUuid, estabelecimento.uuid));

        // Falhou: o modal fica aberto para tentar de novo, com o erro no snackbar.
        if (ok) {
            setBloqueio(undefined);
        }
    };

    if (!inicializado) {
        return (
            <Container sx={{ py: 3 }}>
                <Skeleton variant="rounded" height={120} sx={{ borderRadius: "24px" }} />
                <Skeleton variant="rounded" height={48} sx={{ borderRadius: 2, mt: 3 }} />
                <Skeleton variant="rounded" height={280} sx={{ borderRadius: "20px", mt: 2 }} />
            </Container>
        );
    }

    if (!organizacaoUuid) {
        return (
            <Container sx={{ py: 3 }}>
                <VazioBox>
                    <CorporateFare sx={{ fontSize: 48, color: "text.disabled" }} />
                    <Typography variant="h6">Você não participa de nenhuma organização</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460 }}>
                        Organizações reúnem vários estabelecimentos sob a mesma administração. Fale com o suporte da
                        Space Food para criar a sua.
                    </Typography>
                </VazioBox>
            </Container>
        );
    }

    const documentoOrganizacao = documentoEstabelecimento({ cnpj: organizacao?.cnpj });

    return (
        <Container sx={{ py: 3 }}>
            <Backdrop open={entrando} sx={{ zIndex: theme => theme.zIndex.drawer + 1, color: "#fff" }}>
                <CircularProgress color="inherit" />
            </Backdrop>

            <HeaderPaper variant="outlined">
                <IdentidadeBox>
                    <MonogramaBox>{iniciais(organizacao?.nome)}</MonogramaBox>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h5" fontWeight="bold" noWrap>{organizacao?.nome ?? "Organização"}</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                            {organizacao?.razaoSocial && (
                                <Typography variant="body2" color="text.secondary">{organizacao.razaoSocial}</Typography>
                            )}
                            {documentoOrganizacao && <Chip size="small" variant="outlined" label={documentoOrganizacao} />}
                        </Box>
                    </Box>
                </IdentidadeBox>

                <IndicadoresBox>
                    <Indicador rotulo="Estabelecimentos" valor={String(resumo.total)} />
                    <Indicador rotulo="Com acesso" valor={String(resumo.ativos)} cor="success.main" />
                    <Indicador rotulo="Sem acesso" valor={String(resumo.bloqueados)} cor={resumo.bloqueados ? "error.main" : undefined} />
                    <Indicador rotulo="Receita mensal" valor={real(resumo.receitaMensal)} />
                </IndicadoresBox>

                {minhas.length > 1 && (
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id="organizacao-ativa-label">Organização</InputLabel>
                        <Select
                            labelId="organizacao-ativa-label"
                            label="Organização"
                            value={organizacaoUuid}
                            onChange={(evento) => setOrganizacaoUuid(evento.target.value)}
                        >
                            {minhas.map(item => (
                                <MenuItem key={item.uuid} value={item.uuid}>{item.nome}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </HeaderPaper>

            <Paper variant="outlined" sx={{ borderRadius: 3, mt: 3, px: 2 }}>
                <Tabs value={aba} onChange={(_, valor) => setAba(valor)} variant="scrollable" allowScrollButtonsMobile>
                    <Tab
                        sx={{ textTransform: "none" }}
                        icon={<Badge badgeContent={resumo.total} color="primary" max={99}><Storefront /></Badge>}
                        iconPosition="start"
                        label="Estabelecimentos"
                    />
                    <Tab
                        sx={{ textTransform: "none" }}
                        icon={<CreditCard />}
                        iconPosition="start"
                        label="Assinaturas"
                    />
                    <Tab
                        sx={{ textTransform: "none" }}
                        icon={<Badge badgeContent={membros.length} color="primary" max={99}><Groups /></Badge>}
                        iconPosition="start"
                        label="Usuários"
                    />
                </Tabs>
            </Paper>

            {aba === 0 && (
                <AbaEstabelecimentos
                    estabelecimentos={estabelecimentos}
                    carregando={carregando}
                    entrando={entrando}
                    onEntrar={handleEntrar}
                    onGerenciarAssinatura={abrirAssinatura}
                    onAlterarBloqueio={(estabelecimento, bloquear) => setBloqueio({ estabelecimento, bloquear })}
                    onNovo={() => navigate("/organizacoes/estabelecimentos/criar")}
                />
            )}

            {aba === 1 && (
                <AbaAssinaturas
                    estabelecimentos={estabelecimentos}
                    resumo={resumo}
                    carregando={carregando}
                    onGerenciarAssinatura={abrirAssinatura}
                />
            )}

            {aba === 2 && (
                <AbaUsuarios
                    membros={membros}
                    usuarioAtualUuid={usuario?.uuid}
                    carregando={carregando}
                    salvando={salvando}
                    onAdicionar={handleAdicionarMembro}
                    onRemover={handleRemoverMembro}
                />
            )}

            <ModalAssinatura
                open={!!assinatura}
                nomeEstabelecimento={assinatura ? nomeEstabelecimento(assinatura.estabelecimento) : ""}
                situacao={assinatura?.situacao ?? null}
                planos={planos}
                carregando={!!assinatura?.carregando}
                salvando={salvando}
                onFechar={() => setAssinatura(undefined)}
                onCriar={handleCriarAssinatura}
                onAtualizar={handleAtualizarAssinatura}
                onCancelar={handleCancelarAssinatura}
            />

            <ModalBloqueio
                open={!!bloqueio}
                bloquear={!!bloqueio?.bloquear}
                nomeEstabelecimento={bloqueio ? nomeEstabelecimento(bloqueio.estabelecimento) : ""}
                loading={salvando}
                onConfirmar={handleConfirmarBloqueio}
                onFechar={() => setBloqueio(undefined)}
            />
        </Container>
    );
};
